import { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import { useUrlParams } from '../hooks/useUrlState';
import { localDateStr } from '../utils/dates';
import { useConfirm, useToast } from '../context/FeedbackContext';
import MultiSelectFilter, { listValue, listParam } from '../components/common/MultiSelectFilter';
import EntityLink from '../components/EntityLink';
import StateBlock from '../components/StateBlock';
import Badge from '../components/Badge';

/**
 * Vendor Remittances (2026-09-30): what TMV paid each vendor (or contractor
 * paid directly) in a date range, from VERIFIED payments only, grouped by
 * bank transfer. Per payee: PDF (blocked when "not ready to send" unless
 * overridden), copy-able email text; ZIP of all PDFs and a CSV for the
 * range. Second tab: a vendor statement with running balance.
 * Server: routes/remittances.js, utils/remittances.js.
 */

const money = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const utcStamp = s => (s ? `${s.slice(0, 16)} UTC` : '');

// "Sent" is tracked per payment (remittance_sent_items, 2026-10-01), so a
// payee shows as sent in any date range covering the payments that went out.
function SentStatus({ sent }) {
  if (!sent || !sent.total) return null;
  const who = sent.last_sent_at ? `${utcStamp(sent.last_sent_at)} by ${sent.last_sent_by || '—'}` : '';
  const range = sent.last_range ? (sent.last_range.from === sent.last_range.to ? sent.last_range.from : `${sent.last_range.from} – ${sent.last_range.to}`) : '';
  return (
    <div style={{ marginTop: 4 }}>
      {sent.status === 'sent' && <Badge variant="success">Sent</Badge>}
      {sent.status === 'partly_sent' && <Badge variant="warning">Partly sent ({sent.sent} of {sent.total})</Badge>}
      {sent.status === 'not_sent' && <Badge>Not sent</Badge>}
      {who && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }} title={range ? `On the remittance for ${range}` : ''}>{who}</div>}
    </div>
  );
}

function monthBounds(offset) {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth() + offset, 1);
  const last = new Date(d.getFullYear(), d.getMonth() + offset + 1, 0);
  return [localDateStr(first), localDateStr(last)];
}

// Authenticated file download (PDF / ZIP / CSV) that surfaces the server's
// JSON error message when the request is refused.
async function download(path, params, filename) {
  try {
    const r = await api.get(path, { params, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', filename);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return null;
  } catch (e) {
    try { return JSON.parse(await e.response.data.text()).error; } catch { return e.message || 'Download failed'; }
  }
}

// mailto: link to the payee's email(s) in PARAM with the remittance email
// text (2026-10-22). PARAM never sends email itself; the PDF is attached by
// hand. Returns null when the payee has no email on file.
function mailtoHref(v) {
  const to = String(v.vendor_email || '').split(/[;,\s]+/).filter(e => e.includes('@'));
  if (!to.length) return null;
  const body = v.email.body.replace(/\r?\n/g, '\r\n');
  return `mailto:${to.map(encodeURIComponent).join(',')}?subject=${encodeURIComponent(v.email.subject)}&body=${encodeURIComponent(body)}`;
}

async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch {
    const ta = document.createElement('textarea');
    ta.value = text; document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy'); ta.remove(); return ok;
  }
}

export default function VendorRemittancesPage() {
  const { get, set } = useUrlParams();
  const tab = get('tab', 'remit');
  const today = localDateStr();
  const from = get('from', today);
  const to = get('to', from);
  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Vendor Remittances</h1>
          <p className="page-subtitle">What TMV paid each vendor, from verified payments only — one block per bank transfer.</p>
        </div>
      </div>
      <div className="filter-bar" style={{ marginBottom: 12 }}>
        <button className={'btn btn-sm ' + (tab === 'remit' ? 'btn-primary' : 'btn-secondary')} onClick={() => set({ tab: '' })}>Remittances</button>
        <button className={'btn btn-sm ' + (tab === 'statement' ? 'btn-primary' : 'btn-secondary')} onClick={() => set({ tab: 'statement' })}>Vendor statement</button>
      </div>
      <DateRange from={from} to={to} onChange={(f, t) => set({ from: f === today && t === today ? '' : f, to: t === f ? '' : t })} />
      {tab === 'statement' ? <Statement from={from} to={to} /> : <Remittances from={from} to={to} />}
    </div>
  );
}

function DateRange({ from, to, onChange }) {
  const presets = [
    ['Today', () => { const t = localDateStr(); return [t, t]; }],
    ['Yesterday', () => { const d = new Date(); d.setDate(d.getDate() - 1); const t = localDateStr(d); return [t, t]; }],
    ['This month', () => monthBounds(0)],
    ['Last month', () => monthBounds(-1)],
  ];
  return (
    <div className="filter-bar">
      <label style={{ fontSize: 12, color: 'var(--muted)' }}>From</label>
      <input type="date" value={from} onChange={e => onChange(e.target.value, e.target.value > to ? e.target.value : to)} />
      <label style={{ fontSize: 12, color: 'var(--muted)' }}>To</label>
      <input type="date" value={to} min={from} onChange={e => onChange(from, e.target.value)} />
      {presets.map(([label, fn]) => (
        <button key={label} type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(...fn())}>{label}</button>
      ))}
    </div>
  );
}

function Remittances({ from, to }) {
  const { get, set } = useUrlParams();
  const confirm = useConfirm();
  const toast = useToast();
  const payees = listValue(get('payees', ''));
  const trace = get('trace', '');
  const unsent = get('unsent', '') === '1';
  const [traceInput, setTraceInput] = useState(trace);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [vendors, setVendors] = useState([]);
  const [busy, setBusy] = useState('');
  const [includeFlagged, setIncludeFlagged] = useState(false);

  useEffect(() => { api.get('/vendors').then(r => setVendors(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    setData(null); setError('');
    api.get('/remittances', { params: { from, to, payees: listParam(payees) || undefined, trace: trace || undefined, unsent: unsent ? '1' : undefined } })
      .then(r => setData(r.data)).catch(e => setError(e.response?.data?.error || 'Could not load remittances'));
  }, [from, to, payees.join(), trace, unsent]);

  const payeeOptions = useMemo(() => {
    const opts = new Map(vendors.map(v => [`v${v.id}`, { value: `v${v.id}`, label: v.name }]));
    for (const v of data?.vendors || []) if (!opts.has(v.payee_key)) opts.set(v.payee_key, { value: v.payee_key, label: v.vendor_name });
    return [...opts.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [vendors, data]);

  const params = { from, to, payees: listParam(payees) || undefined, trace: trace || undefined, unsent: unsent ? '1' : undefined };
  const fileRange = from === to ? from : `${from}_${to}`;
  // A download stamps the payments as sent, so re-read the list afterwards.
  const reload = () => api.get('/remittances', { params }).then(r => setData(r.data)).catch(() => {});

  async function pdf(v, override) {
    if (override && !(await confirm({ title: `Send ${v.vendor_name}'s remittance anyway?`, message: 'This payee is flagged "not ready to send":\n\n' + v.flags.map(f => '• ' + f.message).join('\n') + '\n\nDownload the PDF regardless?', confirmLabel: 'Download anyway', danger: true }))) return;
    setBusy('pdf' + v.payee_key);
    const err = await download('/remittances/pdf', { from, to, trace: trace || undefined, unsent: unsent ? '1' : undefined, payee: v.payee_key, override: override ? '1' : undefined },
      `Remittance-${v.vendor_name.replace(/[^A-Za-z0-9]+/g, '-')}-${fileRange}.pdf`);
    setBusy('');
    if (err) toast(err, 'error'); else reload();
  }
  // Marked for this exact range, or stamped by a download for it -> offer Un-mark.
  const markedHere = v => !!(v.log?.marked_sent_at || v.log?.stamped > 0);
  async function toggleSent(v) {
    const sent = !markedHere(v);
    if (!sent && !(await confirm({ title: `Un-mark ${v.vendor_name}?`, message: `Removes the "sent" record of this payee's remittance for ${from === to ? from : `${from} – ${to}`} only. Payments sent on a remittance for another date range stay marked as sent.`, confirmLabel: 'Un-mark' }))) return;
    setBusy('sent' + v.payee_key);
    try {
      const r = await api.post('/remittances/log/sent', { payee: v.payee_key, from, to, sent, unsent: unsent ? '1' : undefined });
      if (unsent) reload();
      else setData(d => ({ ...d, vendors: d.vendors.map(x => x.payee_key === v.payee_key ? { ...x, log: r.data.log, sent: r.data.sent } : x) }));
      toast(sent ? `Marked ${v.vendor_name}'s remittance as sent` : 'Marked as not sent', 'success');
    } catch (e) {
      toast(e.response?.data?.error || 'Could not update', 'error');
    } finally { setBusy(''); }
  }
  async function copyEmail(v) {
    const ok = await copyText(`Subject: ${v.email.subject}\n\n${v.email.body}`);
    toast(ok ? `Email text for ${v.vendor_name} copied` : 'Could not copy - select the text manually', ok ? 'success' : 'error');
  }
  async function zip() {
    setBusy('zip');
    const err = await download('/remittances/zip', { ...params, include_flagged: includeFlagged ? '1' : undefined }, `Remittances-${fileRange}.zip`);
    setBusy('');
    if (err) toast(err, 'error'); else reload();
  }
  async function csv() {
    setBusy('csv');
    const err = await download('/remittances/csv', params, `remittances-${fileRange}.csv`);
    setBusy('');
    if (err) toast(err, 'error');
  }

  return (
    <>
      <div className="filter-bar">
        <MultiSelectFilter label="Vendor" options={payeeOptions} selected={payees} onChange={v => set({ payees: listParam(v) })} />
        <input type="text" className="filter-search" placeholder="PNC trace ID…" value={traceInput}
          onChange={e => setTraceInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') set({ trace: traceInput.trim() }); }}
          onBlur={() => set({ trace: traceInput.trim() })} style={{ maxWidth: 220 }} />
        <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }} title="Only verified payments that are not on any sent remittance yet - for any date range">
          <input type="checkbox" checked={unsent} onChange={e => set({ unsent: e.target.checked ? '1' : '' })} /> Not yet sent
        </label>
        {(payees.length > 0 || trace || unsent) && <button className="btn btn-ghost btn-sm" onClick={() => { setTraceInput(''); set({ payees: '', trace: '', unsent: '' }); }}>Clear filters</button>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={includeFlagged} onChange={e => setIncludeFlagged(e.target.checked)} /> include not-ready in ZIP
          </label>
          <button className="btn btn-secondary btn-sm" onClick={zip} disabled={!data?.vendors?.length || busy === 'zip'}>{busy === 'zip' ? 'Preparing…' : '↓ Download all (ZIP)'}</button>
          <button className="btn btn-secondary btn-sm" onClick={csv} disabled={!data?.vendors?.length || busy === 'csv'}>↓ CSV</button>
        </div>
      </div>

      {error && <StateBlock type="error" message={error} />}
      {!error && !data && <StateBlock type="loading" />}

      {data && data.vendors.some(v => !mailtoHref(v)) && (
        <div className="warning-strip">
          <strong>No email on file</strong> for {data.vendors.filter(v => !mailtoHref(v)).length} payee(s) in this view - add one on the vendor / contractor record to use "Open email":{' '}
          {data.vendors.filter(v => !mailtoHref(v)).map((v, i) => (
            <span key={v.payee_key}>{i > 0 && ', '}{v.payee_type === 'vendor' ? <EntityLink type="vendor" id={v.vendor_id}>{v.vendor_name}</EntityLink> : <EntityLink type="contractor" id={v.contractor_id}>{v.vendor_name}</EntityLink>}</span>
          ))}
        </div>
      )}

      {data && data.unverified.length > 0 && (
        <div className="warning-strip">
          <strong>{data.unverified.length} unverified payment(s) in this range</strong> are not on any remittance until verified:{' '}
          {data.unverified.map((u, i) => (
            <span key={u.payment_id}>{i > 0 && ', '}<EntityLink type="ap_invoice" id={u.ap_invoice_id}>{u.invoice_number}</EntityLink> ({u.vendor_name || '—'}, {money(u.cash)})</span>
          ))}
          <div style={{ fontSize: 11, marginTop: 4 }}>Open an invoice and use <em>Verify</em> on its payment, or use <em>Verify from bank</em> on AP Payments → Payments made.</div>
        </div>
      )}

      {data && data.vendors.length === 0 && (
        <StateBlock type="empty" title={unsent ? 'Nothing left to send' : 'No verified payments'} message={`No verified vendor payments${unsent ? ' still to send' : ''} ${from === to ? 'on ' + from : `between ${from} and ${to}`}${trace ? ` with trace ID ${trace}` : ''}.`} />
      )}

      {data && data.vendors.length > 0 && (
        <>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
            {unsent && <><strong style={{ color: 'var(--text)' }}>Not yet sent only</strong> · </>}
            {data.totals.vendors} payee(s) · <strong style={{ color: 'var(--text)' }}>{data.totals.ready} ready to send</strong>
            {!unsent && <> · {data.vendors.filter(v => v.sent?.status === 'sent').length} sent, {data.vendors.filter(v => v.sent?.status === 'partly_sent').length} partly sent</>}
            {data.totals.vendors > data.totals.ready && <> · {data.totals.vendors - data.totals.ready} not ready</>} · total paid <strong style={{ color: 'var(--text)' }}>{money(data.totals.total_paid)}</strong>
            {data.undated.length > 0 && <> · {data.undated.length} payment(s) elsewhere have no readable date and can't be placed in any range</>}
          </div>
          <table className="data-table">
            <thead>
              <tr><th>Vendor / payee</th><th>Status</th><th style={{ textAlign: 'right' }}>Transfers</th><th style={{ textAlign: 'right' }}>Invoices</th><th style={{ textAlign: 'right' }}>Total paid</th><th>Trace ID(s)</th><th style={{ textAlign: 'right' }}>Actions</th></tr>
            </thead>
            <tbody>
              {data.vendors.map(v => (
                <tr key={v.payee_key}>
                  <td>{v.payee_type === 'vendor' ? <EntityLink type="vendor" id={v.vendor_id}>{v.vendor_name}</EntityLink> : <EntityLink type="contractor" id={v.contractor_id}>{v.vendor_name}</EntityLink>}</td>
                  <td>
                    {v.ready ? <Badge variant="success">Ready</Badge> : (
                      <>
                        <Badge variant="warning">Not ready</Badge>
                        <ul className="flag-list">{v.flags.map(f => <li key={f.code}>{f.message}</li>)}</ul>
                      </>
                    )}
                    {v.warnings?.length > 0 && (
                      <ul className="flag-list" style={{ color: 'var(--warning-text)' }} title="Worth a check before sending - does not block the PDF">
                        {v.warnings.map((w, i) => <li key={w.code + i}>⚠ {w.message}</li>)}
                      </ul>
                    )}
                    <SentStatus sent={v.sent} />
                  </td>
                  <td style={{ textAlign: 'right' }}>{v.transfers}</td>
                  <td style={{ textAlign: 'right' }}>{v.invoices}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{money(v.total_paid)}</td>
                  <td style={{ fontSize: 12, color: 'var(--muted)' }}>{v.trace_ids.join(', ')}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {v.ready
                      ? <button className="btn btn-sm btn-primary" onClick={() => pdf(v, false)} disabled={busy === 'pdf' + v.payee_key}>PDF</button>
                      : <button className="btn btn-sm btn-secondary" onClick={() => pdf(v, true)} disabled={busy === 'pdf' + v.payee_key} title="Not ready to send - download anyway">PDF (override)</button>}
                    {' '}{mailtoHref(v)
                      ? <a className="btn btn-sm btn-secondary" href={mailtoHref(v)} title={`Opens your email app to ${v.vendor_email} - attach the downloaded PDF`}>Open email</a>
                      : <span style={{ fontSize: 11, color: 'var(--warning)' }} title="Add an email on the vendor / contractor record">No email on file</span>}
                    {' '}<button className="btn btn-sm btn-secondary" onClick={() => copyEmail(v)} title={v.email.subject}>Copy email</button>
                    {' '}<button className={'btn btn-sm ' + (markedHere(v) ? 'btn-ghost' : 'btn-secondary')} onClick={() => toggleSent(v)} disabled={busy === 'sent' + v.payee_key}
                      title={markedHere(v) ? 'Un-mark the remittance for this exact date range' : 'Record that this remittance was emailed - stamps every payment on it'}>
                      {markedHere(v) ? 'Un-mark' : 'Mark as sent'}
                    </button>
                    {v.log?.generated_at && !markedHere(v) && (
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>PDF generated {utcStamp(v.log.generated_at)}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

function Statement({ from, to }) {
  const { get, set } = useUrlParams();
  const toast = useToast();
  const payee = get('payee', '');
  const [vendors, setVendors] = useState([]);
  const [st, setSt] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { api.get('/vendors').then(r => setVendors((r.data || []).sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {}); }, []);
  useEffect(() => {
    if (!payee) { setSt(null); return; }
    setSt(null); setError('');
    api.get('/remittances/statement', { params: { payee, from, to } }).then(r => setSt(r.data)).catch(e => setError(e.response?.data?.error || 'Could not load statement'));
  }, [payee, from, to]);

  async function csv() {
    const err = await download('/remittances/statement', { payee, from, to, format: 'csv' }, `statement-${(st?.vendor?.name || 'vendor').replace(/[^A-Za-z0-9]+/g, '-')}-${from}_${to}.csv`);
    if (err) toast(err, 'error');
  }

  return (
    <>
      <div className="filter-bar">
        <select value={payee} onChange={e => set({ payee: e.target.value })} style={{ minWidth: 260 }}>
          <option value="">Choose a vendor…</option>
          {vendors.map(v => <option key={v.id} value={`v${v.id}`}>{v.name}</option>)}
        </select>
        {st && <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={csv}>↓ CSV</button>}
      </div>
      {!payee && <StateBlock type="empty" title="Pick a vendor" message="Every AP invoice and verified payment in the date range, with opening, running and closing balance." />}
      {error && <StateBlock type="error" message={error} />}
      {payee && !st && !error && <StateBlock type="loading" />}
      {st && (
        <>
          {st.unverified.length > 0 && (
            <div className="warning-strip">
              <strong>{st.unverified.length} unverified payment(s)</strong> in this range are not included in the balance: {st.unverified.map(u => `${u.invoice_number} ${money(u.cash)} (${u.date})`).join(', ')}.
            </div>
          )}
          <table className="data-table">
            <thead>
              <tr><th>Date</th><th>Type</th><th>Reference</th><th>Contractor</th><th>Trace ID</th><th style={{ textAlign: 'right' }}>Invoiced</th><th style={{ textAlign: 'right' }}>Paid (cash + deductions)</th><th style={{ textAlign: 'right' }}>Balance</th><th>Remittance sent</th></tr>
            </thead>
            <tbody>
              <tr style={{ fontWeight: 600 }}><td>{st.from}</td><td colSpan={6}>Opening balance</td><td style={{ textAlign: 'right' }}>{money(st.opening)}</td><td /></tr>
              {st.lines.map((l, i) => (
                <tr key={i}>
                  <td>{l.date}</td>
                  <td>{l.kind === 'invoice' ? <Badge>Invoice</Badge> : l.kind === 'advance' ? <Badge variant="warning">Advance paid</Badge> : <Badge variant="success">Payment</Badge>}</td>
                  <td><EntityLink type="ap_invoice" id={l.ap_invoice_id}>{l.ref}</EntityLink></td>
                  <td>{l.contractor_name || '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--muted)' }}>{l.trace_id || ''}</td>
                  <td style={{ textAlign: 'right' }}>{l.debit ? money(l.debit) : ''}</td>
                  <td style={{ textAlign: 'right' }}>
                    {l.credit ? money(l.credit) : ''}
                    {l.kind === 'advance' ? <div style={{ fontSize: 11, color: 'var(--muted)' }}>advance {money(l.cash)} – not an invoice payment</div> : null}
                    {l.kind === 'payment' && (l.epd || l.bgv || l.advance) ? (
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>cash {money(l.cash)}{l.epd ? ` · EPD ${money(l.epd)}` : ''}{l.bgv ? ` · BGV ${money(l.bgv)}` : ''}{l.advance ? ` · adv ${money(l.advance)}` : ''}</div>
                    ) : null}
                  </td>
                  <td style={{ textAlign: 'right' }}>{money(l.balance)}</td>
                  <td style={{ fontSize: 12, color: 'var(--muted)' }} title={l.sent_by ? `by ${l.sent_by}` : ''}>
                    {l.kind === 'payment' ? (l.sent_at ? utcStamp(l.sent_at) : 'Not sent') : ''}
                  </td>
                </tr>
              ))}
              <tr style={{ fontWeight: 700 }}><td>{st.to}</td><td colSpan={4}>Closing balance</td><td style={{ textAlign: 'right' }}>{money(st.total_invoiced)}</td><td style={{ textAlign: 'right' }}>{money(st.total_settled)}</td><td style={{ textAlign: 'right' }}>{money(st.closing)}</td><td /></tr>
            </tbody>
          </table>
          {(st.undated.length > 0 || st.undated_invoices.length > 0) && (
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
              Not placed (no readable date): {[...st.undated.map(u => `payment on ${u.invoice_number} (${money(u.cash)})`), ...st.undated_invoices.map(i => `invoice ${i.invoice_number}`)].join(', ')}.
            </div>
          )}
        </>
      )}
    </>
  );
}
